import React, { createContext, useContext, useEffect, useState } from "react";

const OFFLINE_BOOK_KEY = "offline_saved_book";

interface OfflineContextType {
  offlineEnabled: boolean;
  toggleOffline: () => void;
  showOfflinePrompt: boolean;
  dismissPrompt: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => { return; };

const OfflineContext = createContext<OfflineContextType>({
  offlineEnabled: false,
  toggleOffline: noop,
  showOfflinePrompt: false,
  dismissPrompt: noop
});

export const useOffline = () => useContext(OfflineContext);

export const OfflineProvider = ({ children }: { children: React.ReactNode }) => {
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [showOfflinePrompt, setShowOfflinePrompt] = useState(false);

  const toggleOffline = () => setOfflineEnabled((prev) => !prev);
  const dismissPrompt = () => setShowOfflinePrompt(false);

  useEffect(() => {
    const handleOffline = () => {
      const hasSavedBook = !!localStorage.getItem(OFFLINE_BOOK_KEY);
      if (hasSavedBook && !offlineEnabled) {
        setShowOfflinePrompt(true);
      }
    };
    const handleOnline = () => setShowOfflinePrompt(false);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [offlineEnabled]);

  return (
    <OfflineContext.Provider value={{ offlineEnabled, toggleOffline, showOfflinePrompt, dismissPrompt }}>
      {children}
    </OfflineContext.Provider>
  );
};
